import { AutocompleteChangeReason } from "@mui/material/Autocomplete";
import { SxProps, Theme } from "@mui/material/styles";
import ExtendedPath from "csi/core/ExtendedPath";
import Key from "csi/core/Key";
import PathHints from "csi/core/PathHints";
import Spec from "csi/core/Spec";
import SpecBuilder from "csi/core/SpecBuilder";
import { parseExtendedPath } from "csi/core/extendedPathService";
import csiHeading from "csi/heading/_base";
import { FILTER_CURRENT_USER } from "csi/prefs";
import reflection from "csi/reflection";
import ReflectionClass from "csi/reflection/ReflectionClass";
import { getClass } from "csi/reflection/reflection";
import { featureTogglesStore } from "csi/state/rootStore";
import { StoreValues } from "csi/store/Values";
import { C8AttributeId, C8AttributeKey, C8AttributeValue, C8NodeType, C8PrimitiveValue, attributeValueAsArray } from "csi/typings";
import { isDefined, isNullOrUndefined } from "csi/utility/coreUtility";
import { DateRangeSelection, DynamicDates, dynamicDatesToDateRangeSelectionMap, getDateRange } from "csi/utility/dateUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import { formatNumberForEditing } from "csi/utility/numberUtility";
import { pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import { SplitButtonItem } from "designSystem/components/button/SplitButton";
import { C8DateRange } from "designSystem/components/dateRange/DateRange";
import { DROP_ROOT, DndItemWithId } from "designSystem/dnd/index";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import _, { differenceBy, orderBy, uniqBy } from "lodash";
import { action, computed, makeObservable, observable, override, reaction, runInAction } from "mobx";
import React, { useContext } from "react";
import { DropTargetMonitor } from "react-dnd";
import { ICustomViewFilter, ICustomViewFilterCondition, ICustomViewFilterGroup } from "src/c8-platform/type/customView";
import { Operator } from "src/c8-platform/type/operators";
import { convertSpecToComponentItem, convertValueStoreToComponentFilterItems, organizeComponentItems } from "../common";
import CustomViewAttributesStore from "../customView/attributes/CustomViewAttributesStore";
import { GetValuesStoreFunction, convertRuleToFilterCondition } from "../customView/customViewDataManagementService";
import { getCustomViewPathKeys, getCustomViewPathMetaDataKeys } from "../customView/customViewService";
import { EXCLUDE_BLANK_OPERATOR_LIST, OPERATORS_NOT_APPLICABLE_TO_RELATIVE_FILTER, OPERATORS_THAT_SHOW_MULTI_SELECT, OPERATORS_THAT_SHOW_TEXT_FIELD, RuleSpec, getSpecsForKeys } from "./RuleService";

const BLANKS_COMPONENTITEM: ComponentItem = {
	id: "",
	res: "FILTER_BLANKS",
};

// dnd attribute types for drag and drop
export enum DND_TYPE {
	RULE = "RULE",
	RULE_GROUP = "RULE_GROUP",
}
const AND_OPERATOR_ID = "RuleOperatorAllId";
const OR_OPERATOR_ID = "RuleOperatorAnyId";

/** get rule and index recursively from the map */
function getRuleFromMap(ruleMap: Record<number, Rule | RuleGroup>, rule: Rule | RuleGroup): [number, RuleGroup | null] {
	let sourceIndex = -1;
	let sourceGroup: RuleGroup | null = null;

	for (const [index, mapRule] of Object.entries(ruleMap)) {
		if (mapRule === rule) {
			sourceIndex = parseInt(index);
			break;
		} else if (mapRule instanceof RuleGroup) {
			const [nestedIndex, nestedGroup] = getRuleFromMap(mapRule.ruleMap, rule);
			if (nestedIndex !== -1) {
				sourceGroup = mapRule;
				return [nestedIndex, sourceGroup];
			}
		}
	}

	return [sourceIndex, sourceGroup];
}

/** shift items in the rule map down or up, if a rule is included, that means dropping into the same group */
function shiftItems(ruleMap: Record<number, Rule | RuleGroup>, isDown: boolean, sourceIndex: number, targetIndex?: number, rule?: Rule | RuleGroup): void {
	const keys = Object.keys(ruleMap)
		.map(Number)
		.sort((a, b) => a - b);

	if (isDown) {
		let itemsToShift = keys.slice(targetIndex);
		if (rule) {
			itemsToShift = keys.slice(targetIndex, sourceIndex);
		}
		for (let i = itemsToShift.length - 1; i >= 0; i--) {
			const key = itemsToShift[i];
			const item = ruleMap[key];
			if (item !== rule) {
				ruleMap[key + 1] = item;
				delete ruleMap[key];
			}
		}
	} else {
		let itemsToShift = keys.slice(sourceIndex);
		if (rule) {
			itemsToShift = keys.slice(sourceIndex, targetIndex + 1);
		}
		for (let i = 0; i < itemsToShift.length; i++) {
			const key = itemsToShift[i];
			const item = ruleMap[key];
			if (item !== rule) {
				ruleMap[key - 1] = item;
				delete ruleMap[key];
			}
		}
	}
}

/** drop rule helper function */
function dropRule(ruleMap: Record<number, Rule | RuleGroup>, rule: Rule | RuleGroup, targetIndex: number, groupId: string): void {
	const [sourceIndex, sourceGroup] = getRuleFromMap(ruleMap, rule);
	const ruleMapFrom = sourceGroup?.ruleMap || ruleMap;
	const ruleMapTo = groupId === DROP_ROOT ? ruleMap : ruleMap[groupId].ruleMap;
	const isSameGroup = ruleMapFrom === ruleMapTo;

	// if same group and source / target index, take no action
	if (isSameGroup && sourceIndex === targetIndex) {
		return;
	}

	runInAction(() => {
		if (isSameGroup) {
			// don't need to delete the rule in this case, just overwrite it by shifting
			shiftItems(ruleMapTo, targetIndex < sourceIndex, sourceIndex, targetIndex, rule);
		} else {
			delete ruleMapFrom[sourceIndex];
			// move ruleMapFrom items up after deleting
			shiftItems(ruleMapFrom, false, sourceIndex, targetIndex);
			// push the items down to prepare for inserting
			shiftItems(ruleMapTo, true, sourceIndex, targetIndex);
		}

		// Insert the rule at the target index
		ruleMapTo[targetIndex] = rule;
	});
};

/** Determines whether the user is allowed to drop the item at the specified group and target index. */
function canDrop(ruleMap: Record<number, Rule | RuleGroup>, rule: Rule | RuleGroup, targetIndex: number, groupId: string): boolean {
	if (rule instanceof RuleGroup) {
		return groupId === DROP_ROOT;
	}

	return true;
};

export class Rule extends ReactionStore implements DndItemWithId {
	/** attribute to which the rule will apply */
	@observable.ref item: ComponentItem;

	/** indicates if the rule is valid based on the date */
	@observable isDateValid: boolean = true;

	/** indicate if we're fetching the values for the current rule attribute */
	@observable loading: boolean = false;

	/** parent rule store */
	readonly parentRuleStore: RuleBase;

	/** preselect values for this rule  */
	readonly preselectValues: C8AttributeKey | C8AttributeValue;

	/** indicate if we need to refetch the value list */
	@observable refetchValueList: boolean;

	/** type to which filter are relative  */
	readonly relativeType: C8NodeType;

	/** the selected operator for this rule */
	@observable.ref selectedOperator: ComponentItem;

	/** the selected/input values for this rule */
	// TODO: should be observable array?
	@observable selectedValues: ComponentItem[] = [];

	/** the identifier of the attribute to which the rule would apply */
	@observable.ref spec: Spec;

	/** typed value for number field */
	@observable typedValueForNumberField: string = null;

	/** value list for rule */
	@observable.ref valueList: ComponentItem[] = [];

	/** determines if comparison operators are available for ref and string attributes. True when the custom view is Version 2 or before  */
	restoreTextComparisonOperatorsForOldCV: boolean;

	/** determines whether "is blank" is an operator */
	readonly blankAsValue: boolean;

	readonly loc: ILocaleProvider;

	/** the index of the RuleGroup in the rule map */
	get index(): number {
		const { parentRuleStore } = this;
		let ruleMap = parentRuleStore.ruleMap;
		if (parentRuleStore instanceof RuleGroup) {
			ruleMap = parentRuleStore.ruleStore.ruleMap;
		}
		return getRuleFromMap(ruleMap, this)[0];
	}

	get id(): string {
		return `${this.index}`;
	}

	/** allows filter to select existing value and enter specific values */
	get allowFilteringBySelectionOfExistingValues(): boolean {
		return this.spec.querySpec.allowFilteringBySelectionOfExistingValues;
	}

	/** key to selected attribute for rule */
	get attributeKey(): Key {
		return this.spec.key;
	}

	/** indicates if reference spec is within a matrix */
	@computed get inMatrix(): boolean {
		return this.spec?.querySpec.pathInMatrix != null;
	}

	/** determine the operator is "is blank" or "is not blank" operator */
	get isBlankOperator(): boolean {
		const opId = this.selectedOperator.id;
		return opId.includes(xml.OP_NON_BLANKS) ||
			opId.includes(xml.OP_BLANKS) ||
			opId === xml.OP_NULL ||
			opId === xml.OP_NOT_NULL;
	}

	/** is the operator the equals operator */
	get isEqualsOperator(): boolean {
		return this.selectedOperator.id === xml.OP_EQUALS;
	}

	/** return localized value used to denote current user */
	get valueCurrentUser(): string {
		return this.loc.resource("PreferenceFilterCurrentUser");
	}

	/** indicates if all elements of rule are valid */
	@computed get isValid(): boolean {
		const { selectedValues, isBlankOperator, isValidWithEmptyValues } = this;
		return isValidWithEmptyValues && (selectedValues?.length > 0 || isBlankOperator);
	}

	/** 
	 * indicates if rule is valid, the difference between isValid and isValidWithEmptyValues is that rules with no selected values is also valid in this function.
	 * This is to enable save custom view when we have filtes/rules with no selected values
	 */
	@computed get isValidWithEmptyValues(): boolean {
		const { spec, isDateValid } = this;
		return !!spec && isDateValid;
	}

	/** set the isValid flag to true or false */
	@action setIsDateValid = (isValid: boolean): void => {
		this.isDateValid = isValid;
	};

	/** callback to change the value for multiple select */
	@action onChangeValueMultiSelect = (event: React.ChangeEvent<{}>, values: ComponentItem[], reason: AutocompleteChangeReason): void => {
		this.selectedValues = values;
	};

	/** callback to change the value for single select */
	@action onChangeValueSingleSelect = (event: React.ChangeEvent<{}>, value: ComponentItem, reason: AutocompleteChangeReason): void => {
		if (value) {
			this.selectedValues[0] = value;
		} else {
			this.selectedValues = [];
		}
	};

	/** callback to blur the value for number field */
	@action onBlurValueNumberField = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>): void => {
		const { value } = event.target;

		this.typedValueForNumberField = null;

		const n = parseFloat(value);
		if (isNaN(n)) {
			this.selectedValues = [];
			return;
		}

		const formattedValue = formatNumberForEditing(n, this.spec?.format);

		this.selectedValues[0] = {
			id: formattedValue,
			name: formattedValue,
			value: formattedValue,
		};
	};

	/** callback to change the value for number field */
	@action onChangeValueNumberField = (event: React.ChangeEvent<HTMLInputElement>): void => {
		this.typedValueForNumberField = event.target.value;
	};

	/** callback to change the value for text box  */
	@action onChangeValueTextField = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
		const { value } = event.target;

		this.selectedValues[0] = {
			id: value,
			name: value,
			value,
		};
	};

	/** callback to change the value for date range */
	@action onChangeValueDateRange = (range: C8DateRange | null, dds?: DynamicDates, n?: number): void => {
		const rangeFrom = range?.fromEpochMillis;
		const { isEqualsOperator, spec: { supportsTimeFormat } } = this;
		const selection = dds ? dynamicDatesToDateRangeSelectionMap[dds] : DateRangeSelection.CUSTOM;

		if (!dds && !rangeFrom) {
			this.selectedValues = [];
		} else {
			let rangeTo = range?.toEpochMillis;
			if (isEqualsOperator && !supportsTimeFormat) {
				const dateRange = getDateRange(rangeFrom, rangeTo, selection);
				rangeTo = dateRange[1];
			}
			this.selectedValues = [
				{
					id: `${rangeFrom}`,
					value: rangeFrom,
				},
				{
					id: `${rangeTo}`,
					value: rangeTo,
				},
				{
					id: `${selection}`,
					value: selection,
				},
			];
			if (isDefined(n)) {
				this.selectedValues.push({
					id: `${n}`,
					value: n,
				});
			}
		}
	};

	/** the operators which are applicable for this particular attribute of this rule */
	@computed get operators(): ComponentItem[] {
		const { blankAsValue, relativeType, restoreTextComparisonOperatorsForOldCV, spec } = this;

		if (spec) {
			// situation where blank is a value
			if (blankAsValue) {
				const { ops } = spec.querySpec;
				return ops.map((id) => ({ id, res: spec.getLocalizationResBasedOnTypes(id) } as ComponentItem));
			}

			// situation where "is blank" is an operator
			const restore = restoreTextComparisonOperatorsForOldCV && !featureTogglesStore.RESTORE_TEXT_COMPARISON_OPERATORS;
			let ops: ComponentItem[] = spec.getOperatorStore(restore).data.map(({ id, name }) => ({ id, name }));

			if (!!relativeType) {
				ops = ops.filter((op) => !OPERATORS_NOT_APPLICABLE_TO_RELATIVE_FILTER.has(op.id));
			}

			return ops;
		}
	}

	/** get the relative attribute - either latestRevision or currentRevision based on the class and path hints */
	private getRelativeAttributeBasedOnReflectionClass(rc: ReflectionClass, pathHints: PathHints): C8AttributeId {
		let relAttr = "";
		const masterType = rc.getMasterClass().getType();
		const revisionType = rc.getRevisionClass().getType();
		const currentRevision = pathCreate(reflection.ATTR_CURRENT_REVISION);
		const latestRevision = pathCreate(reflection.ATTR_LATEST_REVISION);
		const currentPath = `(${masterType})${currentRevision}(${revisionType})`;
		const latestPath = `(${masterType})${latestRevision}(${revisionType})`;
		const currentEp = parseExtendedPath(currentPath, pathHints);
		const latestEp = parseExtendedPath(latestPath, pathHints);

		relAttr = currentEp.isOmitted ? reflection.ATTR_LATEST_REVISION : reflection.ATTR_CURRENT_REVISION;

		if (!currentEp.isOmitted && !latestEp.isOmitted) {
			// if both of these paths are exposed, use the latest revision
			relAttr = reflection.ATTR_LATEST_REVISION;
		}

		return relAttr;
	}

	/** relative attribute list for this rule */
	get relativeAttributes(): ComponentItem[] | null {
		const { getRelativeAttributeBasedOnReflectionClass, loc, parentRuleStore, spec: sourceSpec, relativeType } = this;

		if (!relativeType) {
			return null;
		}

		const extendedPath = new ExtendedPath(relativeType);
		const rc = reflection.getClass(relativeType);
		let relAttr;
		let relType;

		if (rc.isRevision()) {
			// apply 'Child:__Parent__' step to get to master attributes
			relAttr = xml.ATTR_PARENT;
			relType = rc.getMasterClass().getType();
		} else if (rc.isMaster()) {
			// apply 'Child:CurrentRevision' or 'Child:LatestRevision' step to get to revision attributes
			relAttr = getRelativeAttributeBasedOnReflectionClass(rc, parentRuleStore.attributeStore?.mainLevel.specBuilder.defaultPathHints);
			relType = rc.getRevisionClass().getType();
		}

		let keys = getCustomViewPathKeys(extendedPath, null, null, null, null, null, null, true);

		if (relAttr) {
			const relPath = new ExtendedPath(relativeType);
			relPath.addRelationStep(relAttr, relType);
			const additionalKeys = Key.getExtendedPathKeys(relPath, true, true);
			keys = keys.concat(additionalKeys);
		}

		// include meta data keys as well
		const metaDataKeys = getCustomViewPathMetaDataKeys(extendedPath);
		keys = keys.concat(metaDataKeys);

		// only need the same type of the selected attribute, image doesn't have "querySpec" here, should filter it out for sure
		const specs = getSpecsForKeys(keys)
			.filter((spec) => !!spec.querySpec && sourceSpec.querySpec.type === spec.querySpec.type);
		const items = orderBy(specs.map((spec) => convertSpecToComponentItem(spec, loc)), ["group", "name"], ["asc", "asc"]);

		return items;
	}

	/** relative helper text shown underneath the filter row  */
	get relativeTypeText(): string | null {
		const { loc, relativeType } = this;

		if (!relativeType) {
			return null;
		}

		return loc.resource("CustomViewAddCustomViewRelativeTypeText", [relativeType]);
	}

	/** rule spec */
	@computed get ruleSpec(): RuleSpec {
		const { isValid, selectedOperator, selectedValues, spec } = this;

		if (isValid) {
			return {
				rule: {
					op: selectedOperator.id,
					values: selectedValues.map((item) => item.id),
				},
				spec,
			};
		}

		return null;
	}

	/** return the value when the selected value is shown in the form of text field */
	get selectedValueForTextField(): string {
		return this.selectedValues[0]?.name ?? "";
	}

	/** return the value of the number field */
	get selectedValueForNumberField(): string {
		return this.selectedValues[0]?.value ?? 0;
	}

	/** return number text field value */
	get numberTextFieldValue(): string {
		const { typedValueForNumberField, selectedValueForNumberField } = this;
		return isNullOrUndefined(typedValueForNumberField) ? selectedValueForNumberField : typedValueForNumberField;
	}

	/** show date picker */
	get showDatePicker(): boolean {
		return !this.isBlankOperator && this.spec?.isDateAttribute() && !this.relativeType;
	}

	/** show date picker */
	get showDatePickerTime(): boolean {
		const { spec } = this;
		return spec?.supportsTimeFormat;
	}

	/** show date range picker */
	get showDateRangePicker(): boolean {
		return this.showDatePicker && this.selectedOperator.id === xml.OP_RANGE;
	}

	/** show multi select for the rule selected value dropdown */
	get showMultiSelect(): boolean {
		const { allowFilteringBySelectionOfExistingValues, isBlankOperator, relativeType, selectedOperator } = this;
		return !isBlankOperator && allowFilteringBySelectionOfExistingValues && OPERATORS_THAT_SHOW_MULTI_SELECT.has(selectedOperator.id) && !relativeType;
	}

	/** indicate if we need to show select component for relative filter or not */
	get isRelativeFilter(): boolean {
		const { relativeType } = this;
		return !!relativeType;
	}

	/** date attributes now contain OP_EQUALS, this has selected operator set to equals / non-equals operator and a non-date attribute */
	get isSelectedOperatorEqualityWithNonDateAttribute(): boolean {
		const { id } = this.selectedOperator;
		return (id === xml.OP_NOT_EQUALS || id === xml.OP_EQUALS) && !this.spec?.isDateAttribute();
	}

	/** show single select for the rule selected value dropdown */
	get showSingleSelect(): boolean {
		const { allowFilteringBySelectionOfExistingValues, isRelativeFilter, isSelectedOperatorEqualityWithNonDateAttribute } = this;
		return isRelativeFilter || (allowFilteringBySelectionOfExistingValues && isSelectedOperatorEqualityWithNonDateAttribute);
	}

	/** text field is allowed to filter and the selected operator can show the text field */
	get allowFilteringAndSelectedOperatorCanShowTextField(): boolean {
		const { allowFilteringBySelectionOfExistingValues, selectedOperator } = this;
		return !allowFilteringBySelectionOfExistingValues || OPERATORS_THAT_SHOW_TEXT_FIELD.has(selectedOperator.id);
	}

	/** show number field for the rule selected value */
	get showNumberField(): boolean {
		const { isBlankOperator, isRelativeFilter, spec } = this;
		const type = spec?.type;
		return !isBlankOperator && !isRelativeFilter && (type === xml.ATTR_DOUBLE || type === xml.ATTR_FLOAT || type === xml.ATTR_INTEGER);
	}

	/** show text field for the rule selected value */
	get showTextField(): boolean {
		const { allowFilteringAndSelectedOperatorCanShowTextField, isRelativeFilter, showNumberField, spec } = this;
		return !isRelativeFilter && !spec?.isDateAttribute() && !showNumberField && allowFilteringAndSelectedOperatorCanShowTextField;
	}

	/** visibility of the select element, do this to reserve a space to avoid the flicker  */
	@computed get visibilityOfSelect(): SxProps<Theme> {
		const { isBlankOperator, relativeType } = this;

		return {
			visibility: (isBlankOperator || !!relativeType) ? "hidden" : "visible",
		};
	}

	/** create new value store for rule */
	@action createValueStore = async (): Promise<void> => {
		const { parentRuleStore, refetchValueList, relativeType, spec, selectedOperator, valueCurrentUser } = this;

		if (!refetchValueList) {
			return;
		}

		try {
			let possibleItems: ComponentItem[];

			if (!relativeType) {
				// normal filter mode 
				// query values
				let valueStore: StoreValues;

				if (spec) {
					valueStore = this.parentRuleStore.getValuesStore(spec);
				}

				if (valueStore) {
					// add special value for Current User if attribute/property points to a user
					if (parentRuleStore.allowCurrentUser && xml.attributeTypeIsRef(spec.type)) {
						const bUser = !!spec.querySpec.prop || getClass("User")
							.isDerivedFrom(spec.rcDest);

						if (bUser) {
							valueStore.addSpecialValue(FILTER_CURRENT_USER, valueCurrentUser);
						}
					}

					this.loading = true;
					possibleItems = await convertValueStoreToComponentFilterItems(valueStore);
				}
			} else {
				// relative filter mode
				possibleItems = this.relativeAttributes;
			}

			runInAction(() => {
				// if no value yet, assign a blank one
				let valueList = _.isEmpty(possibleItems) ? [BLANKS_COMPONENTITEM] : possibleItems;

				if (EXCLUDE_BLANK_OPERATOR_LIST.includes(selectedOperator.id)) {
					valueList = valueList.filter((item) => "" !== item.id);
				}

				this.valueList = valueList;
				this.loading = false;
			});
		} catch (error) {
			console.error(error);
		}

		// reset the flag
		runInAction(() => {
			this.refetchValueList = false;
		});
	};

	/** create the entries for the existing values to pre-select */
	@action private processPreselectedValues = (): void => {
		const { parentRuleStore, preselectValues, spec } = this;

		// preselect the values if configured
		if (!_.isEmpty(preselectValues)) {
			// if the previous value no longer exists, still create it to be able to render it anyway
			this.selectedValues = (preselectValues as C8PrimitiveValue[]).map((preselectedValue) => {
				let relativeValueName;
				if (this.relativeType) {
					const key = Key.toKey(preselectedValue as string);
					relativeValueName = parentRuleStore.attributeStore.mainLevel.specBuilder.getSpecForKey(key).name;
				}

				return {
					id: preselectedValue,
					name: relativeValueName || csiHeading.getTextForFilterValue(null, spec, preselectedValue),
					value: preselectedValue,
				} as ComponentItem;
			});
		}
	};

	constructor(
		parentRuleStore: RuleBase,
		loc: ILocaleProvider,
		spec: Spec,
		item: ComponentItem,
		relativeType?: C8NodeType,
		preselectValues?: C8AttributeKey | C8AttributeValue,
		preselectOperator?: Operator,
		blankAsValue?: boolean,
		restoreTextComparisonOperatorsForOldCV?: boolean,
	) {
		super();
		makeObservable(this);
		this.loc = loc;
		this.spec = spec;
		this.item = item;
		this.preselectValues = attributeValueAsArray(preselectValues);

		if (preselectOperator) {
			this.selectedOperator = { id: preselectOperator, res: spec.getLocalizationResBasedOnTypes(preselectOperator) } as ComponentItem;
		}

		this.blankAsValue = blankAsValue;
		this.restoreTextComparisonOperatorsForOldCV = restoreTextComparisonOperatorsForOldCV;
		this.parentRuleStore = parentRuleStore;
		this.relativeType = relativeType;
		this.refetchValueList = !_.isEmpty(this.preselectValues);

		this.trackReaction(reaction(
			() => this.spec.uniqueId + this.selectedOperator.id,
			() => {
				this.refetchValueList = true;
			},
			{ fireImmediately: true },
		));

		// pre-select the values if any
		this.processPreselectedValues();
	}
}

export class RuleBase extends ReactionStore {
	readonly specs = observable.array<Spec>([], { deep: false });

	/** the condition applied to each rule */
	@observable selectedCondition: ComponentItem<RuleBase>;

	@observable ruleMap: Record<number, Rule | RuleGroup>;

	readonly attributeStore: CustomViewAttributesStore;

	/** determines whether "is blank" is an operator */
	readonly blankAsValue: boolean;

	readonly loc: ILocaleProvider;

	readonly getValuesStore: GetValuesStoreFunction;

	/** type to which filters are relative  */
	@observable relativeType?: C8NodeType;

	/** determines if the custom view will include comparison operators in ref and string attributes */
	readonly restoreTextComparisonOperatorsForOldCV: boolean;

	/** allow filtering by current user for ref attributes and properties */
	allowCurrentUser: boolean;

	/** array of all rules */
	@computed get ruleItems(): (Rule | RuleGroup)[] {
		return Object.values(this.ruleMap);
	}

	/** available actions to add filters */
	get actions(): SplitButtonItem[] {
		const { relativeType } = this;
		return [
			{
				res: "CustomViewAddCustomViewFilter",
			},
			{
				res: "CustomViewAddCustomViewRelativeFilter",
				resParams: [this.loc.type(relativeType)],
			},
		];
	}

	/** get condition operators for the set of rules */
	@computed get allConditions(): ComponentItem[] {
		return [AND_OPERATOR_ID, OR_OPERATOR_ID].map((id) => (
			{ id, name: this.loc.resource(id) }
		)) as ComponentItem[];
	}

	/** map of specs admin configures */
	@computed get specMap(): Map<string, Spec> {
		const { specs } = this;
		const map = new Map<string, Spec>();
		for (const spec of specs) {
			if (spec.canFilter()) {
				map.set(spec.getKey(), spec);
			}
		}
		return map;
	}

	/** the index list of all the rules */
	@computed get indexes(): number[] {
		return Object.keys(this.ruleMap)
			.map((index) => parseInt(index));
	}

	/** the next index to add */
	@computed get nextIndex(): number {
		const { indexes } = this;
		// initially, just return 0
		if (!indexes.length) {
			return 0;
		}
		return _.max(this.indexes) + 1;
	}

	/** count of rules */
	get count(): number {
		return _.reduce(this.ruleMap, (memo, rule) => memo + (rule instanceof RuleGroup ? rule.indexes.length : 1), 0);
	}

	/** indicates if condition for rules is Any (OR) rather than All (AND) */
	get isAnyCondition(): boolean {
		return this.selectedCondition.id === this.allConditions[1].id;
	}

	/** Remove rule */
	@action removeRule = (index: number): void => {
		const { ruleMap } = this;
		const rule = Object.values(ruleMap)[index];
		if (rule) {
			const [sourceIndex, sourceGroup] = getRuleFromMap(ruleMap, rule);
			rule.cleanupReactions();
			delete ruleMap[index];
			shiftItems(ruleMap, false, sourceIndex);
		}
	};

	/** set selected condition between rules */
	@action setSelectedCondition = (condition: ComponentItem): void => {
		this.selectedCondition = condition;
	};

	/** the available options based on selected type */
	@computed get options(): ComponentItem[] {
		const { specs, attributeStore } = this;
		const { selectionSpecs } = this.attributeStore || {};
		const componentItems = [];
		let filteredSelectionSpecs = [];

		if (selectionSpecs) {
			filteredSelectionSpecs = differenceBy(selectionSpecs, specs, "uniqueId");
		}

		const allSpecs = [...specs, ...filteredSelectionSpecs];

		for (const spec of allSpecs) {
			if (spec.canFilter()) {
				componentItems.push(attributeStore ? attributeStore.convertSpecToComponentItemForCustomView(spec) : convertSpecToComponentItem(spec));
			}
		}

		return organizeComponentItems(componentItems);
	}

	/**
	 * Add Rule
	 * @param key the key of the attribute if configured 
	 * @param rule the rule schema if configured
	 */
	@action addRule = (key?: string, rule?: ICustomViewFilterCondition, isRelativeType: boolean = false): void => {
		const { loc, nextIndex, options, blankAsValue, relativeType, restoreTextComparisonOperatorsForOldCV } = this;

		// runInAction is needed to make sure UI would reaction after all changes happen
		runInAction(() => {
			let targetItem = options[0];

			if (key) {
				targetItem = options.find((option) => option.id == key);
				// if target item is not found, get the spec for the key and make it an option
				if (!targetItem) {
					const specBuilder = new SpecBuilder();
					const spec = specBuilder.getSpecForKey(key);
					this.specs.push(spec);
					targetItem = this.options.find((option) => option.id == key);
				}
			}

			const { id } = targetItem;
			const targetSpec = this.specMap.get(id);

			this.ruleMap[nextIndex] = new Rule(
				this,
				loc,
				targetSpec,
				targetItem,
				isRelativeType ? relativeType : "",
				rule?.value,
				rule?.op,
				blankAsValue,
				restoreTextComparisonOperatorsForOldCV,
			);

			this.setSelectedAttribute(targetItem, nextIndex, !!key);
		});
	};

	/** handler for the split button */
	onClickAddFilterButton = (item: SplitButtonItem): void => {
		const { actions, addRule } = this;
		const primaryActionId = actions[0].res;

		if (item.res === primaryActionId) {
			// add normal filter
			addRule();
		} else {
			// all relative filter
			addRule(null, null, true);
		}
	};

	/**
	 * set selected item and reset operator and values
	 * @param item  
	 * @param index 
	 * @param generatingRule  - indicate whether this rule is manipulated by system or user
	 */
	@action setSelectedAttribute = (item: ComponentItem, index: number, generatingRule?: boolean): void => {
		const { ruleMap, specMap } = this;
		const { id } = item;
		const indexInMap = Object.keys(ruleMap)[index];
		const targetRule = ruleMap[indexInMap] as Rule;

		targetRule.item = item;
		targetRule.spec = specMap.get(id);

		if (!generatingRule) {
			targetRule.selectedOperator = targetRule.operators[0];
			targetRule.selectedValues = [];
			targetRule.valueList = [];
		}
	};

	@action setSelectedOperator = (selectedItem: ComponentItem, index: number): void => {
		const { ruleMap } = this;
		const indexInMap = Object.keys(ruleMap)[index];
		const targetRule = ruleMap[indexInMap] as Rule;
		targetRule.selectedOperator = selectedItem;
		targetRule.selectedValues = [];
	};

	constructor(loc: ILocaleProvider, specs: Spec[], getValuesStore: GetValuesStoreFunction, attributeStore?: CustomViewAttributesStore, blankAsValue?: boolean, restoreTextComparisonOperatorsForOldCV?: boolean, allowCurrentUser?: boolean) {
		super();
		this.attributeStore = attributeStore;
		this.blankAsValue = blankAsValue;
		this.loc = loc;
		this.specs.replace(specs);
		this.getValuesStore = getValuesStore;
		this.allowCurrentUser = allowCurrentUser;
		this.restoreTextComparisonOperatorsForOldCV = restoreTextComparisonOperatorsForOldCV;
	}
}

export class RuleGroup extends RuleBase implements DndItemWithId {
	/** rules within this filter group */
	@override ruleMap: Record<number, Rule> = {};

	/** parent rule store */
	readonly ruleStore: RuleStore;

	/** the index of the RuleGroup in the rule map */
	get index(): number {
		return getRuleFromMap(this.ruleStore.ruleMap, this)[0];
	}

	get id(): string {
		return `${this.index}`;
	}

	constructor(
		ruleStore: RuleStore,
		specs: Spec[],
		rules: ICustomViewFilterCondition[],
		getValuesStore: GetValuesStoreFunction,
		relativeType?: C8NodeType,
		blankAsValue?: boolean,
		restoreTextComparisonOperatorsForOldCV?: boolean,
		allowCurrentUser?: boolean,
	) {
		super(ruleStore.loc, specs, getValuesStore, ruleStore.attributeStore, blankAsValue, restoreTextComparisonOperatorsForOldCV, allowCurrentUser);
		this.ruleStore = ruleStore;
		makeObservable(this);

		if (rules) {
			for (const rule of rules) {
				this.addRule(rule.key, rule);
			}
		} else {
			this.addRule();
		}

		this.relativeType = relativeType;
	}
}

class RuleStore extends RuleBase {
	/** queryLevel id */
	readonly queryLevelId?: string;

	/** queryLevel name if there are multiple levels, will be null if there is only one level */
	readonly queryLevelName?: string;

	/** limit to the number of rules */
	readonly ruleLimit = Infinity;

	/** the run time rules which would be populated in advance */
	readonly runTimeRules: ICustomViewFilter = null;

	/** the overall rule map */
	@override ruleMap: Record<number, Rule | RuleGroup> = {};

	/** determines whether we open the confirmation dialog for clearing all rules */
	@observable private openClearAllDialog: boolean = false;

	/** returns whether the clear all filter confirmation dialog is open */
	get isClearAllDialogOpen(): boolean {
		return this.openClearAllDialog;
	}

	/** returns true if there is at least one rule that is relative */
	@computed get hasRelativeRule(): boolean {
		return this.rules.some((rule) => rule.isRelativeFilter);
	}

	/** returns true if there is at least one valid rule defined */
	@computed get hasValidRule(): boolean {
		return this.rules.some((rule) => rule.isValid);
	}

	/** returns false if there is at least one invalid rule defined, rule with empty values are also valid */
	@computed get hasInvalidRule(): boolean {
		return this.rules.some((rule) => !rule.isValidWithEmptyValues);
	}

	/** array of all rules */
	@computed get rules(): Rule[] {
		const rules = [];
		for (const rule of Object.values(this.ruleMap)) {
			if (rule instanceof RuleGroup) {
				Object.values(rule.ruleMap)
					.map((filterGroupRule) => {
						rules.push(filterGroupRule);
					});
			} else {
				rules.push(rule);
			}
		}
		return rules;
	}

	/** array of valid rules */
	@computed get validRules(): Rule[] {
		return this.rules.filter((rule) => rule.isValid);
	}

	/** Indicate whether the user is allowed to drop under the target index and groupid */
	canDrop = (dragItem: RuleGroup, targetIndex: number, groupId: string): boolean => {
		return canDrop(this.ruleMap, dragItem, targetIndex, groupId);
	};

	/** drop rule dnd */
	dropRule = (rule: Rule | RuleGroup, monitor: DropTargetMonitor, targetIndex: number, groupId: string): void => {
		dropRule(this.ruleMap, rule, targetIndex, groupId);
	};

	/** Add RuleGroup to ruleMap */
	@action addRuleGroup = (ruleGroup?: ICustomViewFilterGroup): void => {
		const { allowCurrentUser, nextIndex, specs, blankAsValue, relativeType, restoreTextComparisonOperatorsForOldCV, ruleMap, getValuesStore } = this;

		// runInAction is needed to make sure UI would reaction after all changes happen
		runInAction(() => {
			const ruleGroupInstance = ruleMap[nextIndex] = new RuleGroup(
				this,
				specs,
				ruleGroup?.conditions,
				getValuesStore,
				relativeType,
				blankAsValue,
				restoreTextComparisonOperatorsForOldCV,
				allowCurrentUser,
			);

			ruleGroupInstance.selectedCondition = ruleGroup?.isAND ? this.allConditions[0] : this.allConditions[1];
		});
	};

	@computed get overRuleLimit(): boolean {
		const { count, ruleLimit } = this;
		return count >= ruleLimit;
	}

	/** return the current filter settings */
	get currentFilterSettings(): ICustomViewFilter | undefined {
		const filterConditions: (ICustomViewFilterGroup | ICustomViewFilterCondition)[] = [];
		const { ruleMap, runTimeRules, selectedCondition } = this;

		_.forEach(ruleMap, (rule) => {
			const ruleGroup = rule as RuleGroup;
			if (ruleGroup.ruleMap) {
				const groupConditions: ICustomViewFilterCondition[] = [];
				_.forEach(ruleGroup.ruleMap, (filter) => {
					const filterCondition = convertRuleToFilterCondition(filter);
					if (filterCondition) {
						groupConditions.push(filterCondition);
					}
				});
				filterConditions.push({
					conditions: groupConditions,
					isAND: ruleGroup.selectedCondition.id == AND_OPERATOR_ID,
				} as any as ICustomViewFilterGroup);
			} else {
				const filterCondition = convertRuleToFilterCondition(rule as Rule);
				if (filterCondition) {
					filterConditions.push(filterCondition);
				}
			}
		});

		if (filterConditions.length != 0) {
			return {
				C: filterConditions,
				OR: selectedCondition.id == OR_OPERATOR_ID,
				FD: runTimeRules?.FD,
				L: runTimeRules?.L,
			};
		}
	}

	/** return the current filter settings without empty filters */
	get currentFilterSettingsWithoutEmptyFilters(): ICustomViewFilter | undefined {
		const filterConditions: (ICustomViewFilterGroup | ICustomViewFilterCondition)[] = [];
		const { ruleMap, runTimeRules, selectedCondition } = this;

		_.forEach(ruleMap, (rule) => {
			const ruleGroup = rule as RuleGroup;
			if (ruleGroup.ruleMap) {
				const groupConditions: ICustomViewFilterCondition[] = [];
				_.forEach(ruleGroup.ruleMap, (filter) => {
					const filterCondition = convertRuleToFilterCondition(filter);
					if (filterCondition?.value) {
						groupConditions.push(filterCondition);
					}
				});
				if (filterConditions.length > 0) {
					filterConditions.push({
						conditions: groupConditions,
						isAND: ruleGroup.selectedCondition.id == AND_OPERATOR_ID,
					} as any as ICustomViewFilterGroup);
				}
			} else {
				const filterCondition = convertRuleToFilterCondition(rule as Rule);
				if (filterCondition?.value) {
					filterConditions.push(filterCondition);
				}
			}
		});

		if (filterConditions.length != 0) {
			return {
				C: filterConditions,
				OR: selectedCondition.id == OR_OPERATOR_ID,
				FD: runTimeRules?.FD,
				L: runTimeRules?.L,
			};
		}
	}

	/** Remove all rules */
	@action removeAllRules = (): void => {
		this.ruleMap = {};
	};

	@action setSpecs = (specs: Spec[]): void => {
		this.specs.replace(specs);
	};

	@action setOpenClearAllDialog = (open: boolean): void => {
		this.openClearAllDialog = open;
	};

	constructor(
		loc: ILocaleProvider,
		specs: Spec[],
		limit = Infinity,
		getValuesStore: GetValuesStoreFunction,
		runTimeRules?: ICustomViewFilter,
		blankAsValue?: boolean,
		attributeStore?: CustomViewAttributesStore,
		restoreTextComparisonOperatorsForOldCV?: boolean,
		queryLevelId?: string,
		queryLevelName?: string,
		allowCurrentUser?: boolean,
		relativeType?: C8NodeType,
	) {
		super(loc, specs, getValuesStore, attributeStore, blankAsValue, restoreTextComparisonOperatorsForOldCV, allowCurrentUser);
		makeObservable(this);

		const {
			addRule,
			allConditions,
			setSelectedCondition,
		} = this;

		setSelectedCondition(allConditions[0]);

		this.queryLevelId = queryLevelId;
		this.queryLevelName = queryLevelName;
		this.ruleLimit = limit;
		this.runTimeRules = runTimeRules;
		this.relativeType = relativeType;

		// in the case of edit rule, generate rule lines based on rules
		if (runTimeRules) {
			this.selectedCondition = runTimeRules.OR ? this.allConditions[1] : this.allConditions[0];
			for (const rule of runTimeRules.C) {
				// handles the Rule Group in the filter settings
				if ("conditions" in rule) {
					this.addRuleGroup(rule);
				} else {
					// handles single rule in filter settings
					addRule(rule.key, rule, rule.relative);
				}
			}
		}

		/** add a reaction to update the specMap when the selection specs change */
		this.trackReaction(reaction(
			() => this.attributeStore?.selectionSpecs.length,
			() => {
				this.specs.replace(uniqBy([...this.specs, ...this.attributeStore.selectionSpecs], "uniqueId"));
			},
		));
	}
}

export default RuleStore;

export const RuleStoreContext = React.createContext<RuleStore>(undefined);
export const useRuleStore = (): RuleStore => useContext(RuleStoreContext);